/* Connection History, for a laptop or phone: each network it uses and its
 * traffic, when one went silent or disconnected and came back, where its
 * connections moved, and the modem behind a cellular path. Read from what the
 * device reports to the fleet, so a device that is off shows as it last was.
 * The measurements sit behind one disclosure. */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiClock } from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { buildWanDownIntervals } from "./wanSeries";
import { STATES, clock, happening, headline, lastMove, pathLabel, rate, signalWords } from "./historyWords";
import "./ConnectionHistory.css";

// over is the window as "the last ..." takes it.
const WINDOWS = [
  { key: "15m", label: "15 minutes", over: "15 minutes", ms: 15 * 60e3 },
  { key: "1h", label: "1 hour", over: "hour", ms: 60 * 60e3 },
  { key: "6h", label: "6 hours", over: "6 hours", ms: 6 * 60 * 60e3 },
  { key: "24h", label: "24 hours", over: "24 hours", ms: 24 * 60 * 60e3 },
];

// A device reports every 30 seconds and sends what it has every minute, and a
// path going down, coming back or losing its connections at once. Three
// minutes without a report means it has stopped, and three missed reports in a
// path's traffic are drawn as nothing rather than as the last reading held.
export const STALE_AFTER_MS = 3 * 60e3;
const GAP_MS = 90e3;
const SERIES_POLL_MS = 10e3;
const EVENT_POLL_MS = 2e3;
const TIMEOUT_MS = 20e3;
const EVENT_TYPES = "wan_down,wan_up,failover,cloud_switch";

const at = (ts) => new Date(ts).getTime();
const byTime = (a, b) => at(a.ts) - at(b.ts);

/** pathRows is one row per path seen in the window: its traffic over time,
 *  when it was silent, the moments it went silent and came back, and its state
 *  now. */
export function pathRows(links, events) {
  const rows = new Map();
  const row = (name) => {
    if (!rows.has(name)) {
      rows.set(name, { name, label: pathLabel(name), points: [], marks: [], last: {}, lastTs: 0, state: "working" });
    }
    return rows.get(name);
  };
  for (const s of links) {
    const ts = at(s.ts);
    if (!s.tags?.wan || !Number.isFinite(ts)) continue;
    const r = row(s.tags.wan);
    r.points.push({ ts, value: (s.fields?.tx_bytes_per_sec || 0) + (s.fields?.rx_bytes_per_sec || 0) });
    if (ts >= r.lastTs) Object.assign(r, { last: s.fields || {}, lastTs: ts });
  }
  for (const e of [...events].sort(byTime)) {
    if (!e.tags?.wan || (e.type !== "wan_down" && e.type !== "wan_up")) continue;
    const r = row(e.tags.wan);
    const gone = e.type === "wan_down" && e.tags.cause === "gone";
    if (!gone) r.marks.push({ ts: at(e.ts), up: e.type === "wan_up" });
    r.state = e.type === "wan_up" ? "working" : gone ? "disconnected" : "silent";
  }
  const silent = buildWanDownIntervals(events);
  return [...rows.values()]
    .map((r) => {
      r.points.sort((a, b) => a.ts - b.ts);
      return { ...r, intervals: silent.get(r.name) || [], peak: Math.max(0, ...r.points.map((p) => p.value)) };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** happenings are the window's events as sentences, newest first. */
export function happenings(events) {
  const silent = new Map();
  const out = [];
  for (const e of [...events].sort(byTime)) {
    const wan = e.tags?.wan;
    const said = happening(e, silent.get(wan) === true);
    if (e.type === "wan_down") silent.set(wan, e.tags?.cause !== "gone");
    if (e.type === "wan_up") silent.set(wan, false);
    if (said) out.push({ ...said, ts: e.ts, key: `${e.ts}|${e.type}|${wan || ""}` });
  }
  return out.reverse();
}

/** modems is each modem's newest report, with its signal over the window. A
 *  modem is known by its IMEI, so one that gained a path is still one modem. */
export function modems(samples) {
  const found = new Map();
  for (const s of [...samples].sort(byTime)) {
    const key = s.extra?.imei || s.tags?.wan || "";
    const m = found.get(key) || { key, signal: [] };
    if (Number.isFinite(s.fields?.signal)) m.signal.push({ ts: at(s.ts), value: s.fields.signal });
    Object.assign(m, { wan: s.tags?.wan || "", info: s.extra || {}, now: s.fields?.signal, ts: at(s.ts) });
    found.set(key, m);
  }
  return [...found.values()];
}

// ── Drawing ───────────────────────────────────────────────────────────────────

const W = 600;
const H = 40;

// trace is an area under a series, held from each reading to the next, and
// broken where readings stop.
function trace(points, x, y) {
  let d = "";
  let prev = null;
  for (const p of points) {
    if (!prev || p.ts - prev.ts > GAP_MS) {
      if (prev) d += `L${x(prev.ts)},${H}Z`;
      d += `M${x(p.ts)},${H}L${x(p.ts)},${y(p.value)}`;
    } else {
      d += `L${x(p.ts)},${y(prev.value)}L${x(p.ts)},${y(p.value)}`;
    }
    prev = p;
  }
  return prev ? `${d}L${x(prev.ts)},${H}Z` : "";
}

function Lane({ row, start, end, label }) {
  const x = (t) => ((Math.min(Math.max(t, start), end) - start) / (end - start)) * W;
  const y = (v) => H - (row.peak > 0 ? (v / row.peak) * (H - 4) : 0);
  const shown = row.points.filter((p) => p.ts >= start - GAP_MS);
  return (
    <svg className="ch-lane" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
      {row.intervals.map((iv) => (
        <rect key={iv.start} className="ch-silent" x={x(iv.start)} y={0} height={H}
          width={Math.max(2, x(iv.end === Infinity ? end : iv.end) - x(iv.start))} />
      ))}
      <path className="ch-traffic" d={trace(shown, x, y)} vectorEffect="non-scaling-stroke" />
      {row.marks.filter((m) => m.ts >= start).map((m) => (
        <line key={`${m.ts}${m.up}`} className={m.up ? "ch-mark-up" : "ch-mark-down"} x1={x(m.ts)} x2={x(m.ts)} y1={0} y2={H} vectorEffect="non-scaling-stroke">
          <title>{`${m.up ? "Back" : "Silent"} at ${clock(m.ts)}`}</title>
        </line>
      ))}
    </svg>
  );
}

function PathRow({ row, start, end }) {
  const state = STATES[row.state];
  const latency = row.state === "working" && Number.isFinite(row.last.latency_ms) ? `, ${Math.round(row.last.latency_ms)} ms` : "";
  return (
    <div className="ch-row">
      <div className="ch-row-name">{row.label}</div>
      <Lane row={row} start={start} end={end} label={`${row.label}: traffic, and when nothing got through`} />
      <div className={`ch-row-now tone-${state.tone}`}>
        <strong>{state.word}{latency}</strong>
        <span>{row.peak > 0 ? `busiest ${rate(row.peak)}` : "no traffic"}</span>
      </div>
    </div>
  );
}

function Modem({ modem, start, end, span }) {
  const { info } = modem;
  const signal = { points: modem.signal, peak: 31, intervals: [], marks: [] };
  const name = [info.manufacturer, info.model].filter(Boolean).join(" ") || "A cellular modem";
  const on = [info.carrier && `on ${info.carrier}`, info.radio].filter(Boolean).join(", ");
  return (
    <div className="ch-modem">
      <p className="ch-modem-says">
        <strong>{name}</strong>{on ? ` ${on}` : ""}{modem.wan ? `, carrying ${pathLabel(modem.wan)}` : ", carrying no path now"}.
        {" "}Signal {signalWords(modem.now)}.
      </p>
      {modem.signal.length > 0 && <div className="ch-row">
        <div className="ch-row-name">Signal</div>
        <Lane row={signal} start={start} end={end} label={`Signal over the last ${span}, 0 to 31`} />
        <div className="ch-row-now"><span>0 to 31, over the last {span}</span></div>
      </div>}
      <dl>
        <dt>IMEI</dt><dd>{info.imei || "not reported"}</dd>
        <dt>SIM (ICCID)</dt><dd>{info.iccid || "not reported"}</dd>
        <dt>Firmware</dt><dd>{info.firmware || "not reported"}</dd>
      </dl>
    </div>
  );
}

export default function ConnectionHistoryPanel({ selectedNode, serverApiUrl }) {
  const [windowKey, setWindowKey] = useState("1h");
  const [series, setSeries] = useState(null);
  const [events, setEvents] = useState(null);
  const [errors, setErrors] = useState({});
  const span = WINDOWS.find((w) => w.key === windowKey);

  // ask reads one telemetry query, recording its failure under what it is for.
  const ask = useCallback(async (what, path) => {
    try {
      const res = await fetchWithTimeout(serverApiUrl(path), { credentials: "same-origin" }, TIMEOUT_MS);
      if (!res.ok) throw new Error(`the server answered ${res.status}`);
      const body = await res.json();
      setErrors((e) => ({ ...e, [what]: "" }));
      return body;
    } catch (err) {
      setErrors((e) => ({ ...e, [what]: err.message || String(err) }));
      return null;
    }
  }, [serverApiUrl]);

  const node = encodeURIComponent(selectedNode || "");
  const loadSeries = useCallback(async () => {
    if (!selectedNode) return;
    const [links, modem] = await Promise.all([
      ask("series", `/telemetry/timeseries?node=${node}&metric=wan_link&since=${windowKey}&limit=20000`),
      ask("series", `/telemetry/timeseries?node=${node}&metric=modem&since=${windowKey}&limit=5000`),
    ]);
    if (links && modem) setSeries({ links: links.samples || [], modem: modem.samples || [], at: Date.now() });
  }, [ask, node, selectedNode, windowKey]);
  const loadEvents = useCallback(async () => {
    if (!selectedNode) return;
    const body = await ask("events", `/telemetry/events?node=${node}&since=${windowKey}&type=${EVENT_TYPES}&limit=500`);
    if (body) setEvents(body.events || []);
  }, [ask, node, selectedNode, windowKey]);

  useVisibleInterval(loadSeries, SERIES_POLL_MS, Boolean(selectedNode));
  useVisibleInterval(loadEvents, EVENT_POLL_MS, Boolean(selectedNode));
  useEffect(() => {
    setSeries(null);
    setEvents(null);
    loadSeries();
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, windowKey]);

  const view = useMemo(() => {
    if (!series || !events) return null;
    const rows = pathRows(series.links, events);
    const heard = Math.max(0, ...rows.map((r) => r.lastTs), ...series.modem.map((s) => at(s.ts)).filter(Number.isFinite));
    return { rows, heard, items: happenings(events), modems: modems(series.modem), move: lastMove(events) };
  }, [series, events]);
  const error = errors.events || errors.series;

  const end = Date.now();
  const start = end - span.ms;

  return (
    <div className="panel-card ch-panel">
      <header className="ch-toolbar">
        <span className="ch-freshness" role="status">
          <FiClock aria-hidden="true" />
          {!view ? "Reading what this device reported" : view.heard ? `From its report at ${clock(view.heard)}. A device reports about once a minute.` : "Nothing reported in this window"}
        </span>
        <div className="ch-range" role="group" aria-label="Time range">
          {WINDOWS.map((w) => (
            <button key={w.key} aria-pressed={w.key === windowKey} onClick={() => setWindowKey(w.key)}>{w.label}</button>
          ))}
        </div>
      </header>
      {error && <div className="ch-note" role="alert">Could not read this device's history: {error}.{view && " Showing the last answer."}</div>}
      {view && <>
        <section className="ch-headline">
          <h3>{headline(view.rows, view.heard, Date.now(), STALE_AFTER_MS)}</h3>
          {view.move && <p>{view.move}</p>}
        </section>

        <section className="ch-section" aria-labelledby="ch-paths">
          <h3 id="ch-paths">Its networks</h3>
          <p className="ch-lead">Traffic over the last {span.over}. Red is a stretch when nothing got through; a line marks the moment a network went silent or came back.</p>
          {view.rows.length ? view.rows.map((r) => <PathRow key={r.name} row={r} start={start} end={end} />)
            : <p className="ch-empty">No network reported in this window.</p>}
        </section>

        <section className="ch-section" aria-labelledby="ch-happened">
          <h3 id="ch-happened">What happened</h3>
          <p className="ch-lead">Newest first, in your local time, to the tenth of a second.</p>
          {view.items.length ? (
            <ol className="ch-events">
              {view.items.map((i) => <li key={i.key} className={`tone-${i.tone}`}><time dateTime={i.ts}>{clock(i.ts)}</time><span>{i.text}</span></li>)}
            </ol>
          ) : <p className="ch-empty">Nothing went silent, disconnected or moved in the last {span.over}.</p>}
        </section>

        {view.modems.length > 0 && (
          <section className="ch-section" aria-labelledby="ch-modem">
            <h3 id="ch-modem">{view.modems.length === 1 ? "The modem" : "The modems"}</h3>
            {view.modems.map((m) => <Modem key={m.key} modem={m} start={start} end={end} span={span.over} />)}
          </section>
        )}

        <details className="ch-raw">
          <summary>Show the measurements</summary>
          <p>For whoever looks after the network: each network's last report.</p>
          <table>
            <thead><tr><th scope="col">Network</th><th scope="col">Round trip</th><th scope="col">Jitter</th><th scope="col">Probes lost</th><th scope="col">Up</th><th scope="col">Down</th></tr></thead>
            <tbody>
              {view.rows.map((r) => (
                <tr key={r.name}>
                  <td>{r.label} ({r.name})</td>
                  <td>{Number.isFinite(r.last.latency_ms) ? `${r.last.latency_ms.toFixed(1)} ms` : "not measured"}</td>
                  <td>{Number.isFinite(r.last.jitter_ms) ? `${r.last.jitter_ms.toFixed(1)} ms` : "not measured"}</td>
                  <td>{Number.isFinite(r.last.loss_ratio) ? `${(100 * r.last.loss_ratio).toFixed(0)}%` : "not measured"}</td>
                  <td>{rate(r.last.tx_bytes_per_sec)}</td>
                  <td>{rate(r.last.rx_bytes_per_sec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {view.modems.length > 0 && <p>A modem's signal runs from 0 to 31, each step about 2 dB above -113 dBm.</p>}
        </details>
      </>}
    </div>
  );
}
